import React, { useState, Suspense } from 'react';
import { Outlet, createBrowserRouter } from 'react-router-dom';
import MenuIcon from '@mui/icons-material/Menu';
import { ThemeProvider } from '@emotion/react';
import RedirectLegacyVersion from './redirect';
import { ProtectedRoute } from './../services/security/ProtectedRoute';
import { ACCESS_MENU_ROLES } from 'config/roles';
import { buildRouteChildren, buildPublicRoutes } from './routeBuilder';

// V2
import theme from 'themes/v2/AppTheme';
import MenuV2 from 'global/v2/layouts/Menu';
import ContainerColumn from 'components/v2/layout/ContainerColumn';
import ErrorPage from 'global/v2/pages/ErrorPage/ErrorPage';

// V3 (bascule via features.flags)
import { FLAGS } from 'config/features.flags';
import MenuV3 from 'global/v3/layouts/Menu';

import OnboardingTutorialModalV3 from 'components/v3/modals/OnboardingTutorialModal';
import OnboardingTutorialModalV2 from 'components/v2/modals/OnboardingTutorialModal';
import { useAppContext } from 'services/context/AppContext';
import { useOnboarding } from 'services/context/useOnboarding';
import { useUser } from 'services/context/UserContext';

const Menu = FLAGS.USE_V3_LAYOUT ? MenuV3 : MenuV2;

function Root() {

    const [isSidebarOpen, setIsSidebarOpen] = useState(false);
    const [menuWidth, setMenuWidth] = useState(FLAGS.USE_V3_LAYOUT ? 220 : 0);
    const { showOnboarding, closeTutorial, setShowOnboarding, showOnboardingV2, closeTutorialV2 } = useAppContext();
    const user = useUser();
    const { hasSeenTutorial, isLoading, trackSlideView, markAsCompleted, saveOnClose, startTimer, incrementViews } = useOnboarding(user.user);

    React.useEffect(() => {
        if (!isLoading && !hasSeenTutorial && user.user?.id) {
            setShowOnboarding(true);
        }
    }, [isLoading, hasSeenTutorial, user.user?.id, setShowOnboarding]);

    return <>
        <ThemeProvider theme={theme}>
            <ProtectedRoute allowedRoles={ACCESS_MENU_ROLES}>
                <Menu open={isSidebarOpen} setOpen={setIsSidebarOpen} onWidthChange={setMenuWidth} />
                <ContainerColumn style={{ marginLeft: menuWidth, transition: 'margin-left 0.2s ease' }}>

                    {!FLAGS.USE_V3_LAYOUT && !isSidebarOpen && (
                        <button
                            onClick={() => setIsSidebarOpen(true)}
                            style={{
                                position: 'fixed',
                                top: '10px',
                                left: '10px',
                                zIndex: 1000,
                                background: 'transparent',
                                border: 'none',
                                cursor: 'pointer',
                            }}
                        >
                            <MenuIcon />
                        </button>
                    )}
                    <Suspense fallback={null}>
                        <Outlet />
                    </Suspense>
                </ContainerColumn>
            </ProtectedRoute>

            <OnboardingTutorialModalV3
                open={showOnboarding}
                onClose={closeTutorial}
                trackSlideView={trackSlideView}
                markAsCompleted={markAsCompleted}
                saveOnClose={saveOnClose}
                startTimer={startTimer}
                incrementViews={incrementViews}
                userRoles={user.user?.roles}
            />

            {/* Ancien tutoriel du dashboard V2, garde pour comparaison manuelle avec le tutoriel
                V3 ci-dessus. Ouverture volontaire uniquement (entree "Tutoriel V2" du menu, retiree) :
                ne touche pas au suivi de progression stocke pour le tutoriel V3. */}
            <OnboardingTutorialModalV2
                open={showOnboardingV2}
                onClose={closeTutorialV2}
                trackSlideView={() => {}}
                markAsCompleted={async () => {}}
                saveOnClose={async () => {}}
                startTimer={() => {}}
                incrementViews={() => {}}
                title="Tutoriel MOBIUSpack V2 (ancien)"
            />
        </ThemeProvider>
    </>
}

const Router = createBrowserRouter([
    {
        // Compat anciens liens /v2/* -> /*
        path: '/v2/*',
        element: <RedirectLegacyVersion />,
        errorElement: <ErrorPage />
    },
    {
        path: `/`,
        element: <Root />,
        errorElement: <ErrorPage />,
        children: [
            ...buildRouteChildren(),
            {
                path: '*',
                element: <ErrorPage />
            }
        ],
    },
    ...buildPublicRoutes().map((r) => ({ ...r, errorElement: <ErrorPage /> })),
], {
    future: {
        v7_fetcherPersist: true,
        v7_normalizeFormMethod: true,
        v7_partialHydration: true,
        v7_relativeSplatPath: true,
        v7_skipActionErrorRevalidation: true,
        v7_startTransition: true,
    },
});

export default Router
